import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Container, Form, Row, Spinner, Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import ScopeIndicator from '../components/ScopeIndicator';
import { useAuth } from '../hooks/useAuth';
import { faceImageAPI } from '../utils/api';
import type { ChildAggregate, ChildrenAggregateFilters } from '../types/faceimage';
import { formatDateTimeInNurseryTimezone, getTodayDateInNurseryTimezone } from '../utils/timezone';

type AttendanceStatus = 'Present' | 'Absent' | 'Half Day' | 'Holiday' | 'Vacation' | 'Not Scheduled';

const allowedStatuses: AttendanceStatus[] = ['Present', 'Absent', 'Half Day', 'Holiday', 'Vacation', 'Not Scheduled'];

const normalizeStatus = (value?: string | null): AttendanceStatus | null => {
  if (!value) return null;
  const match = allowedStatuses.find((status) => status.toLowerCase() === value.toLowerCase());
  return match || null;
};

const getStatus = (row: ChildAggregate): AttendanceStatus => {
  const apiStatus = normalizeStatus(row.status);
  if (apiStatus) return apiStatus;
  if (row.is_holiday === true || row.is_holiday === 'true' || row.is_holiday === 'Holiday') return 'Holiday';
  if (row.is_holiday === 'vacation') return 'Vacation';

  const detections = Number(row.total_detections || 0);
  if (!Number.isFinite(detections) || detections <= 0) return 'Absent';
  if (detections === 1 || !row.last_detection || row.first_detection === row.last_detection) return 'Half Day';
  return 'Present';
};

const statusVariant: Record<AttendanceStatus, string> = {
  Present: 'success',
  Absent: 'danger',
  'Half Day': 'warning',
  Holiday: 'info',
  Vacation: 'primary',
  'Not Scheduled': 'secondary',
};

const statusKeys: Record<AttendanceStatus, string> = {
  Present: 'statusPresent',
  Absent: 'statusAbsent',
  'Half Day': 'statusHalfDay',
  Holiday: 'statusHoliday',
  Vacation: 'statusVacation',
  'Not Scheduled': 'statusNotScheduled',
};

const DailyAttendance: React.FC = () => {
  const { t } = useTranslation('pages', { keyPrefix: 'dailyAttendance' });
  const { t: tc } = useTranslation('common');
  const { currentNursery } = useAuth();
  const nurseryTimezone = currentNursery?.timezone || 'UTC';

  const [date, setDate] = useState(() => getTodayDateInNurseryTimezone(nurseryTimezone));
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | AttendanceStatus>('all');
  const [rows, setRows] = useState<ChildAggregate[]>([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setDate(getTodayDateInNurseryTimezone(nurseryTimezone));
  }, [nurseryTimezone]);

  const loadAttendance = useCallback(async () => {
    if (!currentNursery) return;

    setLoading(true);
    setError(null);
    try {
      const filters: ChildrenAggregateFilters = { attendDate: date };
      const response = await faceImageAPI.getChildrenAttendance(currentNursery.nursery_id, filters, true);
      setRows(response.success ? response.data : []);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || tc('errors.error'));
    } finally {
      setLoading(false);
    }
  }, [currentNursery, date, tc]);

  useEffect(() => {
    loadAttendance();
  }, [loadAttendance]);

  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return rows.filter((row) => {
      const status = getStatus(row);
      const matchesStatus = statusFilter === 'all' || status === statusFilter;
      const matchesSearch =
        !term ||
        row.child_name?.toLowerCase().includes(term) ||
        row.child_id?.toLowerCase().includes(term) ||
        row.classroom_name?.toLowerCase().includes(term);

      return matchesStatus && matchesSearch;
    });
  }, [rows, search, statusFilter]);

  const totals = useMemo(() => {
    return filteredRows.reduce<Record<AttendanceStatus, number>>((acc, row) => {
      acc[getStatus(row)] += 1;
      return acc;
    }, {
      Present: 0,
      Absent: 0,
      'Half Day': 0,
      Holiday: 0,
      Vacation: 0,
      'Not Scheduled': 0,
    });
  }, [filteredRows]);

  const formatDateTime = (value?: string | null) => {
    if (!value || value === 'vacation') return '-';
    return formatDateTimeInNurseryTimezone(value, nurseryTimezone);
  };

  const getDropoffTime = (row: ChildAggregate) => row.dropoff_time || row.drop_off_time || row.first_detection;
  const getPickupTime = (row: ChildAggregate) => row.pickup_time || row.pick_up_time || row.last_detection;

  const handleExport = async () => {
    if (!currentNursery) return;

    setExporting(true);
    setError(null);
    try {
      const blob = await faceImageAPI.getChildrenAttendanceCSV(
        currentNursery.nursery_id,
        { attendDate: date },
        nurseryTimezone,
      );
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `daily-attendance-${date}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || tc('errors.error'));
    } finally {
      setExporting(false);
    }
  };

  return (
    <Layout>
      <Container fluid className="py-4">
        <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
          <div>
            <h2 className="fw-bold text-primary mb-1">{t('title', { defaultValue: 'Daily Attendance' })}</h2>
            <div className="text-muted">{currentNursery?.name || currentNursery?.nursery_id}</div>
          </div>
          <Button variant="outline-primary" onClick={handleExport} disabled={!currentNursery || exporting || loading}>
            {exporting ? <Spinner animation="border" size="sm" /> : t('exportCsv', { defaultValue: 'Export CSV' })}
          </Button>
        </div>

        <ScopeIndicator resource="attendanceRecord" />

        {!currentNursery && (
          <Alert variant="warning">{t('selectNursery', { defaultValue: 'Select a nursery to view attendance.' })}</Alert>
        )}

        {error && <Alert variant="danger">{error}</Alert>}

        <Card className="shadow-sm border-0 mb-3">
          <Card.Body>
            <Row className="g-3 align-items-end">
              <Col md={3}>
                <Form.Label>{t('dateLabel', { defaultValue: 'Date' })}</Form.Label>
                <Form.Control type="date" value={date} onChange={(event) => setDate(event.target.value)} />
              </Col>
              <Col md={4}>
                <Form.Label>{t('searchLabel', { defaultValue: 'Search' })}</Form.Label>
                <Form.Control
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder={t('searchPlaceholder', { defaultValue: 'Child, ID, or classroom' })}
                />
              </Col>
              <Col md={3}>
                <Form.Label>{t('statusLabel', { defaultValue: 'Status' })}</Form.Label>
                <Form.Select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value as any)}>
                  <option value="all">{t('allStatuses', { defaultValue: 'All statuses' })}</option>
                  {Object.keys(statusVariant).map((status) => (
                    <option key={status} value={status}>{t(statusKeys[status as AttendanceStatus])}</option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={2}>
                <Button className="w-100" onClick={loadAttendance} disabled={loading || !currentNursery}>
                  {loading ? <Spinner animation="border" size="sm" /> : tc('buttons.refresh', { defaultValue: 'Refresh' })}
                </Button>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Row className="g-3 mb-3">
          {Object.entries(totals).map(([status, count]) => (
            <Col key={status} xs={6} md={2}>
              <Card className="shadow-sm border-0 h-100">
                <Card.Body className="py-3">
                  <div className="text-muted small">{t(statusKeys[status as AttendanceStatus])}</div>
                  <div className="fs-4 fw-bold">{count}</div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>

        <Card className="shadow-sm border-0">
          <Card.Body className="p-0">
            <div className="table-responsive">
              <Table hover className="mb-0 align-middle">
                <thead className="table-light">
                  <tr>
                    <th>{t('child', { defaultValue: 'Child' })}</th>
                    <th>{t('classroomColumn', { defaultValue: 'Classroom' })}</th>
                    <th>{t('status', { defaultValue: 'Status' })}</th>
                    <th>{t('dropoff', { defaultValue: 'Drop-off' })}</th>
                    <th>{t('pickup', { defaultValue: 'Pick-up' })}</th>
                    <th>{t('detectionsColumn', { defaultValue: 'Detections' })}</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={6} className="text-center py-5">
                        <Spinner animation="border" />
                      </td>
                    </tr>
                  ) : filteredRows.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center text-muted py-5">
                        {t('noRecords', { defaultValue: 'No attendance records found.' })}
                      </td>
                    </tr>
                  ) : (
                    filteredRows.map((row) => {
                      const status = getStatus(row);
                      return (
                        <tr key={`${row.child_id}-${row.child_name}-${row.classroom_name}`}>
                          <td>
                            <div className="fw-semibold">{row.child_name}</div>
                            {row.child_id && <small className="text-muted">{row.child_id}</small>}
                          </td>
                          <td>{row.classroom_name || '-'}</td>
                          <td><Badge bg={statusVariant[status]}>{t(statusKeys[status])}</Badge></td>
                          <td>{formatDateTime(getDropoffTime(row))}</td>
                          <td>{formatDateTime(getPickupTime(row))}</td>
                          <td>{row.total_detections ?? 0}</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>
      </Container>
    </Layout>
  );
};

export default DailyAttendance;
